import { ColumnParams, CommonColumnParamsState } from "@org-avp/avp-avengers-ui-shared-utils";
import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { GetStateFn } from "../../utils/dispatchUtils";

export const geometryManagerOptionsSliceName = "geometryManager";

export enum AssemblyManagerColumn {
  GROUPING = "grouping",
  CHANGE_DATE = "changeDate",
  CONDITION = "condition",
  CREATED_AT = "createdAt",
  DELETE_REPRESENTATION = "deleteRepresentation",
  RANK = "rank",
  DOCUMENT_NAME = "documentName",
  DOCUMENT_NUMBER = "documentNumber",
  TRIGGER = "trigger",
  VALIDITY = "validity",
  DND = "dnd",
}

export const assemblyMangerColumnIds = [
  AssemblyManagerColumn.GROUPING,
  AssemblyManagerColumn.DELETE_REPRESENTATION,
  AssemblyManagerColumn.RANK,
  AssemblyManagerColumn.DOCUMENT_NUMBER,
  AssemblyManagerColumn.DOCUMENT_NAME,
  AssemblyManagerColumn.CHANGE_DATE,
  AssemblyManagerColumn.CREATED_AT,
  AssemblyManagerColumn.VALIDITY,
  AssemblyManagerColumn.TRIGGER,
  AssemblyManagerColumn.CONDITION,
  AssemblyManagerColumn.DND,
];

export type GeometryManagerOptionsState = {
  assemblyManager: AssemblyManagerOptions;
};

export type AssemblyManagerOptions = CommonColumnParamsState;

const persistedOptions = localStorage.getItem(geometryManagerOptionsSliceName);
const persistedState = persistedOptions ? JSON.parse(persistedOptions) as GeometryManagerOptionsState : null;

const fallback: GeometryManagerOptionsState = {
  assemblyManager: {
    columns: assemblyMangerColumnIds,
    params: Object.fromEntries(assemblyMangerColumnIds.map((columnId) => [columnId, { columnId: columnId }])),
  },
};

// TODO implement method who can do this automatically for all types of slice data
function buildInitialState(): GeometryManagerOptionsState {
  const initialState: GeometryManagerOptionsState = persistedState ?? fallback;

  // if no assembly manager data exists, add the fallback value
  if (initialState.assemblyManager === undefined)
    initialState.assemblyManager = fallback.assemblyManager;

  // if no assembly manager columns data exists, add the fallback value
  if (initialState.assemblyManager.columns === undefined || initialState.assemblyManager.columns.length === 0)
    initialState.assemblyManager.columns = fallback.assemblyManager.columns;

  if (initialState.assemblyManager.params === undefined)
    initialState.assemblyManager.params = fallback.assemblyManager.params;

  if (initialState !== persistedState) {
    // persist state if items have been added to the persisted object, defer due to function definition order
    setTimeout(() => persistState(() => ({
      [geometryManagerOptionsSliceName]: initialState,
    })));
  }

  return initialState;
}

const geometryManagerOptionsSlice = createSlice({
  name: geometryManagerOptionsSliceName,
  initialState: buildInitialState(),
  reducers: {
    setLayoutProfileString(_, action: PayloadAction<string>) {
      return JSON.parse(action.payload);
    },
    setAssemblyManagerColumns(state: GeometryManagerOptionsState, { payload: columns }: PayloadAction<string[]>) {
      state.assemblyManager.columns = columns;
    },
    updateColumnWidth(state: GeometryManagerOptionsState, action: PayloadAction<ColumnParams>) {
      const params = state.assemblyManager.params ?? {};
      params[action.payload.columnId] = action.payload;
      state.assemblyManager.params = params;
    },
  },
});

export type GeometryManagerOptionsRootState = { [geometryManagerOptionsSliceName]: GeometryManagerOptionsState };
export const selectGeometryManagerOptions = (state: GeometryManagerOptionsRootState) => state[geometryManagerOptionsSliceName];
export const selectAssemblyManagerOptions = (state: GeometryManagerOptionsRootState) => state[geometryManagerOptionsSliceName].assemblyManager;
export const selectGeometryManagerLayoutProfileString = (state: GeometryManagerOptionsRootState) => JSON.stringify(state[geometryManagerOptionsSliceName]);
export const setGeometryManagerLayoutProfileString = (layoutProfileString: string) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(geometryManagerOptionsSlice.actions.setLayoutProfileString(layoutProfileString));
  persistState(getState);
};
export const setAssemblyManagerColumns = (columns: string[]) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(geometryManagerOptionsSlice.actions.setAssemblyManagerColumns(columns));
  persistState(getState);
};
export const updateAssemblyManagerColumnWidth = (columnId: string, columnWidth?: number) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(geometryManagerOptionsSlice.actions.updateColumnWidth({ columnId, width: columnWidth }));
  persistState(getState);
};

function persistState(getState: GetStateFn) {
  localStorage.setItem(geometryManagerOptionsSliceName, JSON.stringify(
    selectGeometryManagerOptions(getState()),
  ));
}

export const geometryManagerOptionsReducer = geometryManagerOptionsSlice.reducer;
